import React, { useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import { Bell, CheckCheck, MessageSquare, ShieldAlert } from 'lucide-react';
import { timeAgo } from '../../utils/formatDate';

const NotificationList = ({ onClose }) => {
  const { notifications, markAsRead, markAllAsRead } = useContext(NotificationContext);

  return (
    <div className="absolute right-0 mt-2 w-[340px] sm:w-[380px] rounded-2xl bg-white border border-slate-200/60 shadow-[0_16px_40px_-15px_rgba(0,0,0,0.15)] overflow-hidden z-50 animate-fadeIn origin-top-right">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center">
            <Bell className="w-3.5 h-3.5 text-slate-700" />
          </div>
          <h4 className="text-[14px] font-semibold text-slate-900 font-display tracking-tight">Notifications</h4>
        </div>
        <button
          onClick={markAllAsRead}
          className="text-[10px] font-bold text-slate-500 hover:text-green-600 uppercase tracking-wider flex items-center space-x-1 px-2.5 py-1.5 rounded-lg hover:bg-green-50 transition-colors"
        >
          <CheckCheck className="w-3.5 h-3.5" />
          <span>Mark all read</span>
        </button>
      </div>

      <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100 custom-scrollbar bg-white">
        {notifications.length === 0 ? (
          <div className="py-10 flex flex-col items-center justify-center text-center">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center mb-2">
              <Bell className="w-4 h-4 text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-700">You're all caught up!</p>
            <p className="text-xs text-slate-400 mt-0.5">No new notifications.</p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n._id}
              onClick={() => markAsRead(n._id)}
              className={`px-4 py-3.5 transition-all cursor-pointer group flex items-start gap-3 relative overflow-hidden ${
                !n.read ? 'bg-green-50/30 hover:bg-green-50/60' : 'bg-white hover:bg-slate-50'
              }`}
            >
              {/* Unread Accent Line */}
              {!n.read && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-green-500" />
              )}

              {/* Notification Icon */}
              <div className={`mt-0.5 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center shadow-sm ${
                !n.read ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-400'
              }`}>
                {n.title.toLowerCase().includes('alert') ? (
                  <ShieldAlert className="w-4 h-4" />
                ) : (
                  <MessageSquare className="w-4 h-4" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center justify-between mb-0.5">
                  <p className={`text-[13px] truncate pr-2 ${!n.read ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`}>
                    {n.title}
                  </p>
                  <span className={`text-[10px] font-medium flex-shrink-0 whitespace-nowrap ${!n.read ? 'text-green-600' : 'text-slate-400'}`}>
                    {timeAgo(n.createdAt)}
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 leading-snug line-clamp-2 pr-1">
                  {n.message}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationList;
